import React, { useState } from 'react';
// Imports for the new Google library and the button component
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode"; // To decode the Google JWT token

import { Shield, User, AtSign, Lock, Phone, School, Users } from 'lucide-react';

// --- Sign Up Form Component (Translated & Modified) ---
const SignUpForm = ({ onSwitchToLogin, onLoginSuccess }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [college, setCollege] = useState('');
    const [trustedEmails, setTrustedEmails] = useState(['']);
    const [error, setError] = useState('');

    const handleAddEmail = () => {
        if (trustedEmails.length < 3) {
            setTrustedEmails([...trustedEmails, '']);
        }
    };

    const handleRemoveEmail = (index) => {
        const newEmails = trustedEmails.filter((_, i) => i !== index);
        setTrustedEmails(newEmails);
    };

    const handleEmailChange = (index, value) => {
        const newEmails = [...trustedEmails];
        newEmails[index] = value;
        setTrustedEmails(newEmails);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!firstName || !lastName || !email || !phone || !college || trustedEmails.some(e => e.trim() === '')) {
            setError('Please fill out all fields.');
            return;
        }
        setError('');
        console.log('Sign Up Data:', { firstName, lastName, email, phone, college, trustedEmails });
        alert("Registration successful. You can now log in.");
        onSwitchToLogin();
    };

    // **NEW**: Handler for successful Google login/signup
    const handleGoogleLoginSuccess = (credentialResponse) => {
        const credentialResponseDecoded = jwtDecode(credentialResponse.credential);
        console.log("Google User Data:", credentialResponseDecoded);

        // You can handle the user data here, like sending it to your backend
        // to create a session or register the user if they are new.

        alert(`Login successful. Welcome, ${credentialResponseDecoded.name}!`);
        onLoginSuccess(); // Call the main app's success function
    };

    // **NEW**: Handler for failed Google login/signup
    const handleGoogleLoginError = () => {
        console.log('Google login failed');
        setError('Google login failed. Please try again.');
    };


    return (
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-lg">
            <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-600">Create an Account</h2>
            </div>
            {error && <p className="text-sm text-center text-red-500 bg-red-100 p-2 rounded-md">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative flex items-center"><User className="h-5 w-5 text-gray-400 absolute left-2" /><input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                    <div className="relative flex items-center"><User className="h-5 w-5 text-gray-400 absolute left-2" /><input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                </div>
                <div className="relative flex items-center"><AtSign className="h-5 w-5 text-gray-400 absolute left-2" /><input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                <div className="relative flex items-center"><Phone className="h-5 w-5 text-gray-400 absolute left-2" /><input type="tel" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                <div className="relative flex items-center"><School className="h-5 w-5 text-gray-400 absolute left-2" /><input type="text" placeholder="College Name" value={college} onChange={(e) => setCollege(e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center"><Users className="mr-2 h-5 w-5" /> Trusted Friends' Emails (1-3)</label>
                    {trustedEmails.map((email, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            <div className="relative flex-grow flex items-center"><AtSign className="h-5 w-5 text-gray-400 absolute left-2" /><input type="email" placeholder={`Trusted Email #${index + 1}`} value={email} onChange={(e) => handleEmailChange(index, e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                            {trustedEmails.length > 1 && (<button type="button" onClick={() => handleRemoveEmail(index)} className="px-2 py-1 text-sm font-medium text-red-600 hover:text-red-800">Remove</button>)}
                        </div>
                    ))}
                    {trustedEmails.length < 3 && (<button type="button" onClick={handleAddEmail} className="w-full text-sm text-indigo-600 hover:text-indigo-800 font-medium py-1">+ Add another email</button>)}
                </div>
                <button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">Sign Up</button>
            </form>

            {/* --- SEPARATOR AND GOOGLE BUTTON --- */}
            <div className="flex items-center justify-center space-x-2 my-4">
                <div className="h-px bg-gray-300 flex-grow"></div>
                <span className="text-gray-500 text-sm">OR</span>
                <div className="h-px bg-gray-300 flex-grow"></div>
            </div>
            <div className="flex justify-center">
                <GoogleLogin
                    onSuccess={handleGoogleLoginSuccess}
                    onError={handleGoogleLoginError}
                />
            </div>
            {/* ------------------------------------ */}

            <p className="text-center text-sm text-gray-600">Already have an account?{' '}<button onClick={onSwitchToLogin} className="font-medium text-indigo-600 hover:text-indigo-500">Log In</button></p>
        </div>
    );
};


// --- Login Form Component (Translated & Modified) ---
const LoginForm = ({ onLoginSuccess, onSwitchToSignUp }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter your email and password.');
            return;
        }
        setError('');
        console.log('Login Data:', { email, password });
        // Traditional authentication logic here
        onLoginSuccess();
    };

    // Handler for successful Google login
    const handleGoogleLoginSuccess = (credentialResponse) => {
        const credentialResponseDecoded = jwtDecode(credentialResponse.credential);
        console.log("Google User Data:", credentialResponseDecoded);
        alert(`Login successful. Welcome, ${credentialResponseDecoded.name}!`);
        onLoginSuccess();
    };

    // Handler for failed Google login
    const handleGoogleLoginError = () => {
        console.log('Google Login Failed');
        setError('Google login failed. Please try again.');
    };

    return (
        <div className="w-full max-w-sm p-8 space-y-6 bg-white rounded-lg shadow-lg">
            <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-600">Welcome Back</h2>
            </div>
            {error && <p className="text-sm text-center text-red-500 bg-red-100 p-2 rounded-md">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative flex items-center"><AtSign className="h-5 w-5 text-gray-400 absolute left-2" /><input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                <div className="relative flex items-center"><Lock className="h-5 w-5 text-gray-400 absolute left-2" /><input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-8 block w-full rounded-md border-gray-300 shadow-sm h-10" /></div>
                <button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">Log In</button>
            </form>

            {/* --- SEPARATOR AND GOOGLE BUTTON --- */}
            <div className="flex items-center justify-center space-x-2 my-4">
                <div className="h-px bg-gray-300 flex-grow"></div>
                <span className="text-gray-500 text-sm">OR</span>
                <div className="h-px bg-gray-300 flex-grow"></div>
            </div>
            <div className="flex justify-center">
                <GoogleLogin
                    onSuccess={handleGoogleLoginSuccess}
                    onError={handleGoogleLoginError}
                    useOneTap // Optional: for a one-tap sign-in experience
                />
            </div>
            {/* ------------------------------------ */}

            <p className="text-center text-sm text-gray-600">Don't have an account?{' '}<button onClick={onSwitchToSignUp} className="font-medium text-indigo-600 hover:text-indigo-500">Sign Up</button></p>
        </div>
    );
};

// --- Main Login Page Component (Translated & Modified) ---
const WesipsafeLogin = ({ onLoginSuccess }) => {
    const [view, setView] = useState('login'); // 'login' or 'signup'
    // **IMPORTANT**: Replace 'YOUR_GOOGLE_CLIENT_ID' with your actual ID.
    const googleClientId = 'YOUR_GOOGLE_CLIENT_ID.apps.googleusercontent.com';

    return (
        // Wrap everything with GoogleOAuthProvider and pass your client ID
        <GoogleOAuthProvider clientId={googleClientId}>
            <div className="flex flex-col justify-center items-center w-full min-h-screen bg-gray-50 p-4">
                <div className="text-center mb-8">
                    <div className="flex justify-center items-center mb-4">
                        <Shield className="mr-2 h-8 w-8 text-indigo-600" />
                        <h1 className="text-4xl font-bold text-gray-800">SipSafe</h1>
                    </div>
                    <p className="text-gray-600">Your safety, our priority.</p>
                </div>
                {view === 'login' ? (
                    <LoginForm onLoginSuccess={onLoginSuccess} onSwitchToSignUp={() => setView('signup')} />
                ) : (
                    <SignUpForm onSwitchToLogin={() => setView('login')} onLoginSuccess={onLoginSuccess} />
                )}
            </div>
        </GoogleOAuthProvider>
    );
};

export default WesipsafeLogin;